import axios from "axios";
import { findCountryCode } from "../utils/CountryCodes";
import { toNationalPhone } from "../utils/PhoneUtils";

const API_KEY = "Sh7yxT0sFO1aujXJhKfo537kU0RgYkwTNdybZWH2";
const AUTH_BASE_PATH = "/v1/auth";
const LEGACY_SIGNIN_FLAG_KEY = "asdasd123123dfgdfgqweqwe";
const SESSION_TOKEN_KEY = "tdp_session_access_token";
const SESSION_CACHE_TTL_MS = 60 * 1000;

const normalizeEmail = (value) => String(value || "").trim().toLowerCase();

const getStoredToken = () => localStorage.getItem(SESSION_TOKEN_KEY) || "";

let sessionCache = null;
let sessionInFlightPromise = null;

const setSessionCache = (session) => {
  if (!session) return;
  const accessToken = session.accessToken || getStoredToken();
  sessionCache = {
    value: accessToken ? { ...session, accessToken } : session,
    cachedAt: Date.now(),
  };
};

const getSessionFromCache = () => {
  if (!sessionCache?.value) return null;
  if (Date.now() - sessionCache.cachedAt > SESSION_CACHE_TTL_MS) return null;
  return sessionCache.value;
};

const clearSessionCache = () => {
  sessionCache = null;
  sessionInFlightPromise = null;
};

const setStoredSession = (accessToken) => {
  localStorage.setItem(LEGACY_SIGNIN_FLAG_KEY, "true");
  localStorage.setItem(SESSION_TOKEN_KEY, accessToken);
};

const clearStoredSession = () => {
  localStorage.removeItem(LEGACY_SIGNIN_FLAG_KEY);
  localStorage.removeItem(SESSION_TOKEN_KEY);
  clearSessionCache();
};

const buildHeaders = ({ withAuth = false, contentType = "application/json" } = {}) => {
  const headers = {
    "x-api-key": API_KEY,
  };
  if (contentType) {
    headers["Content-Type"] = contentType;
  }
  if (withAuth) {
    const token = getStoredToken();
    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }
  }
  return headers;
};

const mapSessionUserToAuthenticatedUser = (sessionUser) => ({
  username: sessionUser?.username || sessionUser?.email || "",
  challengeName: null,
  attributes: {
    email: sessionUser?.email || "",
    name: sessionUser?.contactName || "",
    "custom:company": sessionUser?.company || "",
    "custom:country": sessionUser?.country || "",
    website: sessionUser?.website || "",
    phone_number: sessionUser?.phoneNumber || "",
    address: sessionUser?.address || "",
    "custom:sendEmail": sessionUser?.sendEmail || "NO",
    "custom:channel": sessionUser?.channel || "",
  },
});

const fetchSession = async ({ forceRefresh = false } = {}) => {
  if (!forceRefresh) {
    const cached = getSessionFromCache();
    if (cached) return cached;
    if (sessionInFlightPromise) return sessionInFlightPromise;
  }

  sessionInFlightPromise = axios
    .get(`${AUTH_BASE_PATH}/session`, {
      headers: buildHeaders({ withAuth: true }),
    })
    .then((response) => {
      if (!response.data) {
        throw new Error("No active session");
      }
      setSessionCache(response.data);
      return response.data;
    })
    .finally(() => {
      sessionInFlightPromise = null;
    });

  return sessionInFlightPromise;
};

const signIn = async (username, password) => {
  const response = await axios.post(
    `${AUTH_BASE_PATH}/sign-in`,
    {
      email: normalizeEmail(username),
      password,
      rememberMe: false,
    },
    {
      headers: buildHeaders(),
    },
  );

  if (response.data?.accessToken) {
    setStoredSession(response.data.accessToken);
  }

  if (response.data?.user) {
    setSessionCache({ user: response.data.user });
  }

  if (
    response.data?.challengeName === "MIGRATED_ACCOUNT_VERIFICATION_REQUIRED" ||
    response.data?.challengeName === "RESET_PASSWORD_REQUIRED"
  ) {
    return {
      challengeName: response.data.challengeName,
      username: normalizeEmail(username),
      attributes: {
        email: normalizeEmail(username),
      },
    };
  }

  return mapSessionUserToAuthenticatedUser(response.data?.user);
};

const verifyMigratedAccountEmail = async (username, code) =>
  axios
    .post(
      `${AUTH_BASE_PATH}/migration/verify-email`,
      { email: normalizeEmail(username), code },
      { headers: buildHeaders() },
    )
    .then((response) => response.data);

const completeMigratedAccount = async (username, code, newPassword) => {
  const response = await axios.post(
    `${AUTH_BASE_PATH}/migration/complete`,
    {
      email: normalizeEmail(username),
      code,
      newPassword,
      rememberMe: false,
    },
    { headers: buildHeaders() },
  );
  if (response.data?.accessToken) {
    setStoredSession(response.data.accessToken);
    setSessionCache(response.data);
  }
  return mapSessionUserToAuthenticatedUser(response.data?.user);
};

const sendEmailVerificationCode = async (email) =>
  axios
    .post(
      `${AUTH_BASE_PATH}/resend-sign-up-code`,
      { email: normalizeEmail(email) },
      { headers: buildHeaders() },
    )
    .then((response) => response.data);

const signOut = async () => {
  try {
    const token = getStoredToken();
    if (token) {
      await axios.post(
        `${AUTH_BASE_PATH}/sign-out`,
        {},
        {
          headers: buildHeaders({ withAuth: true }),
        },
      );
    }
  } finally {
    clearStoredSession();
  }
};

const currentAuthenticatedUser = async () => {
  if (!getStoredToken()) {
    throw new Error("No active session");
  }

  try {
    const session = await fetchSession();
    return mapSessionUserToAuthenticatedUser(session.user);
  } catch (error) {
    clearStoredSession();
    throw error;
  }
};

const resolvePhone = (authenticatedUser) => {
  const phoneNumber = authenticatedUser?.attributes?.phone_number;
  if (phoneNumber == null) return null;

  const countryName = authenticatedUser?.attributes?.["custom:country"];
  const countryCode = countryName ? findCountryCode(countryName) : null;
  const phoneCode = countryCode?.phoneCode || "1";
  return toNationalPhone(phoneNumber, phoneCode);
};

const currentAuthenticatedUserAttributes = async () => {
  const authenticatedUser = await currentAuthenticatedUser();
  const phoneWithoutInternationalCode = resolvePhone(authenticatedUser);
  return {
    username: authenticatedUser.attributes.email,
    password: "********",
    contactName:
      authenticatedUser.attributes.name != null
        ? authenticatedUser.attributes.name
        : "",
    company:
      authenticatedUser.attributes["custom:company"] != null
        ? authenticatedUser.attributes["custom:company"]
        : "",
    country:
      authenticatedUser.attributes["custom:country"] != null
        ? authenticatedUser.attributes["custom:country"]
        : "",
    phone: phoneWithoutInternationalCode,
    email:
      authenticatedUser.attributes.email != null
        ? authenticatedUser.attributes.email
        : "",
    website:
      authenticatedUser.attributes.website != null
        ? authenticatedUser.attributes.website
        : "",
    phoneNumber:
      authenticatedUser.attributes.phone_number != null
        ? authenticatedUser.attributes.phone_number
        : "",
    address:
      authenticatedUser.attributes.address != null
        ? authenticatedUser.attributes.address
        : "",
    sendEmail:
      authenticatedUser.attributes["custom:sendEmail"] != null
        ? authenticatedUser.attributes["custom:sendEmail"]
        : "NO",
    channel:
      authenticatedUser.attributes["custom:channel"] != null
        ? authenticatedUser.attributes["custom:channel"]
        : "",
  };
};

const getCachedUserAttributes = () => {
  const cachedSession = getSessionFromCache();
  if (!cachedSession?.user) return null;
  const authenticatedUser = mapSessionUserToAuthenticatedUser(cachedSession.user);
  const phoneWithoutInternationalCode = resolvePhone(authenticatedUser);

  return {
    username: authenticatedUser.attributes.email,
    password: "********",
    contactName:
      authenticatedUser.attributes.name != null
        ? authenticatedUser.attributes.name
        : "",
    company:
      authenticatedUser.attributes["custom:company"] != null
        ? authenticatedUser.attributes["custom:company"]
        : "",
    country:
      authenticatedUser.attributes["custom:country"] != null
        ? authenticatedUser.attributes["custom:country"]
        : "",
    phone: phoneWithoutInternationalCode,
    email:
      authenticatedUser.attributes.email != null
        ? authenticatedUser.attributes.email
        : "",
    website:
      authenticatedUser.attributes.website != null
        ? authenticatedUser.attributes.website
        : "",
    phoneNumber:
      authenticatedUser.attributes.phone_number != null
        ? authenticatedUser.attributes.phone_number
        : "",
    address:
      authenticatedUser.attributes.address != null
        ? authenticatedUser.attributes.address
        : "",
    sendEmail:
      authenticatedUser.attributes["custom:sendEmail"] != null
        ? authenticatedUser.attributes["custom:sendEmail"]
        : "NO",
    channel:
      authenticatedUser.attributes["custom:channel"] != null
        ? authenticatedUser.attributes["custom:channel"]
        : "",
  };
};

const updateUserAttributes = async (user) => user;

const currentUserInfo = async () => currentAuthenticatedUser();
const currentUserPoolUser = async () => currentAuthenticatedUser();
const currentCredentials = async () => ({});
const essentialCredentials = async (credentials) => credentials;
const userSession = async () => fetchSession();
const currentSession = async () => fetchSession();
const federatedSignIn = async () => {
  throw new Error("Not implemented");
};

const forgotPassword = async (username) =>
  axios
    .post(
      `${AUTH_BASE_PATH}/password/forgot`,
      { email: normalizeEmail(username) },
      { headers: buildHeaders() },
    )
    .then((response) => response.data);

const forgotPasswordSubmit = async (username, code, newPassword) =>
  axios.post(
    `${AUTH_BASE_PATH}/password/forgot/confirm`,
    { email: normalizeEmail(username), code, newPassword },
    { headers: buildHeaders() },
  );

const changePassword = async (_user, oldPassword, newPassword) =>
  axios.post(
    `${AUTH_BASE_PATH}/password/change`,
    { currentPassword: oldPassword, newPassword },
    { headers: buildHeaders({ withAuth: true }) },
  );

const completeNewPassword = async (_user, newPassword) => {
  await axios.post(
    `${AUTH_BASE_PATH}/password/set`,
    { newPassword },
    { headers: buildHeaders({ withAuth: true }) },
  );
  return currentAuthenticatedUser();
};

const signUp = async (signUpAttributes) =>
  axios
    .post(
      `${AUTH_BASE_PATH}/sign-up`,
      {
        email: normalizeEmail(signUpAttributes.username),
        password: signUpAttributes.password,
        invitationCode: String(signUpAttributes.invitationCode || "").trim(),
      },
      { headers: buildHeaders() },
    )
    .then((response) => response.data);

const confirmSignUp = async (username, verificationCode) =>
  axios
    .post(
      `${AUTH_BASE_PATH}/confirm-sign-up`,
      {
        email: normalizeEmail(username),
        code: verificationCode,
      },
      { headers: buildHeaders() },
    )
    .then((response) => response.data);

const changeEmail = async (newEmail) =>
  axios
    .post(
      `${AUTH_BASE_PATH}/email/change/start`,
      { newEmail: normalizeEmail(newEmail) },
      { headers: buildHeaders({ withAuth: true }) },
    )
    .then((response) => response.data);

const confirmEmailChange = async (code) =>
  axios
    .post(
      `${AUTH_BASE_PATH}/email/change/confirm`,
      { code },
      { headers: buildHeaders({ withAuth: true }) },
    )
    .then((response) => response.data);

const AuthService = {
  signIn,
  signOut,
  currentAuthenticatedUser,
  currentAuthenticatedUserAttributes,
  getCachedUserAttributes,
  updateUserAttributes,
  currentUserInfo,
  currentUserPoolUser,
  currentCredentials,
  essentialCredentials,
  userSession,
  currentSession,
  federatedSignIn,
  forgotPassword,
  forgotPasswordSubmit,
  verifyMigratedAccountEmail,
  completeMigratedAccount,
  changePassword,
  completeNewPassword,
  signUp,
  confirmSignUp,
  sendEmailVerificationCode,
  changeEmail,
  confirmEmailChange,
};

export default AuthService;
